import { RegistrationStep } from 'types/enums/forms/registration/registrationWizard';
import {
    PERSONAL_DETAILS_PHRASES,
    PROFESSIONAL_DETAILS_PHRASES,
    CONTACT_DETAILS_PHRASES,
    ADDRESS_PHRASES,
} from 'consts/phrases/registrationPhrases';
import { IRegistrationPersonalDetails, IRegistrationProfessionalDetails } from 'types/types';
import { ModelData } from 'types/types/forms/modelData';
import { FORM_ERROR_PHRASES } from 'consts/phrases/errorPhrases';

export const registerFormModel: {
    formId: string;
    [RegistrationStep.PersonalDetails]: ModelData<
        keyof Omit<IRegistrationPersonalDetails, 'externalUserId' | 'externalSourceId'>
    >;
    [RegistrationStep.ProfessionalDetails]: ModelData<
        keyof Pick<
            IRegistrationProfessionalDetails,
            | 'specialtyId'
            | 'medicalNumber'
            | 'stateMedicalCouncil'
            | 'medicalNumberNPI'
            | 'medicalNumberOptOut'
            | 'graduationYear'
            | 'medicalSchool'
            | 'stateCodeLicensed'
            | 'primaryWorkplace'
            | 'primaryWorkplacePhone'
            | 'professionalIdNumber'
        >
    >;
    [RegistrationStep.ContactDetails]: ModelData;
    [RegistrationStep.Identity]: ModelData;
    [RegistrationStep.Address]: ModelData;
} = {
    formId: 'registerForm',
    [RegistrationStep.PersonalDetails]: {
        firstName: {
            name: 'firstName',
            label: PERSONAL_DETAILS_PHRASES.FIRST_NAME,
            requiredErrorMsg: PERSONAL_DETAILS_PHRASES.FIRST_NAME_REQUIRED_ERROR,
            invalidErrorMsg: PERSONAL_DETAILS_PHRASES.FIRST_NAME_INVALID_ERROR,
        },
        lastName: {
            name: 'lastName',
            label: PERSONAL_DETAILS_PHRASES.LAST_NAME,
            requiredErrorMsg: PERSONAL_DETAILS_PHRASES.LAST_NAME_REQUIRED_ERROR,
            invalidErrorMsg: PERSONAL_DETAILS_PHRASES.LAST_NAME_INVALID_ERROR,
        },
        email: {
            name: 'email',
            label: PERSONAL_DETAILS_PHRASES.EMAIL,
            requiredErrorMsg: PERSONAL_DETAILS_PHRASES.EMAIL_REQUIRED_ERROR,
            invalidErrorMsg: PERSONAL_DETAILS_PHRASES.EMAIL_INVALID_ERROR,
        },
        emailConfirm: {
            name: 'emailConfirm',
            label: PERSONAL_DETAILS_PHRASES.CONFIRM_EMAIL,
            requiredErrorMsg: PERSONAL_DETAILS_PHRASES.CONFIRM_EMAIL_REQUIRED_ERROR,
            invalidErrorMsg: PERSONAL_DETAILS_PHRASES.CONFIRM_EMAIL_INVALID_ERROR,
        },
        password: {
            name: 'password',
            label: PERSONAL_DETAILS_PHRASES.PASSWORD,
            requiredErrorMsg: PERSONAL_DETAILS_PHRASES.PASSWORD_REQUIRED_ERROR,
            invalidErrorMsg: PERSONAL_DETAILS_PHRASES.PASSWORD_INVALID_ERROR,
        },
        passwordConfirm: {
            name: 'passwordConfirm',
            label: PERSONAL_DETAILS_PHRASES.CONFIRM_PASSWORD,
            requiredErrorMsg: PERSONAL_DETAILS_PHRASES.CONFIRM_PASSWORD_REQUIRED_ERROR,
            invalidErrorMsg: PERSONAL_DETAILS_PHRASES.CONFIRM_PASSWORD_INVALID_ERROR,
        },
        countryCode: {
            name: 'countryCode',
            label: PERSONAL_DETAILS_PHRASES.COUNTRY,
            requiredErrorMsg: PERSONAL_DETAILS_PHRASES.COUNTRY_REQUIRED_ERROR,
        },
        professionId: {
            name: 'professionId',
            label: PERSONAL_DETAILS_PHRASES.PROFESSION,
            requiredErrorMsg: PERSONAL_DETAILS_PHRASES.PROFESSION_REQUIRED_ERROR,
        },
        howHeardId: {
            name: 'howHeardId',
            label: PERSONAL_DETAILS_PHRASES.HOW_HEARD,
        },
        preferredLanguageId: {
            name: 'preferredLanguageId',
            label: PERSONAL_DETAILS_PHRASES.PREFERRED_LANGUAGE,
            requiredErrorMsg: PERSONAL_DETAILS_PHRASES.PREFERRED_LANGUAGE_REQUIRED_ERROR,
        },
        marketResearch: {
            name: 'marketResearch',
            label: PERSONAL_DETAILS_PHRASES.MARKET_RESEARCH_OPT_IN,
            requiredErrorMsg: PERSONAL_DETAILS_PHRASES.MARKET_RESEARCH_REQUIRED_ERROR,
        },
        consents: {
            name: 'consents',
            label: PERSONAL_DETAILS_PHRASES.CONSENTS,
            requiredErrorMsg: PERSONAL_DETAILS_PHRASES.CONSENTS_REQUIRED_ERROR,
        },
        age: {
            name: 'age',
            label: PERSONAL_DETAILS_PHRASES.AGE,
            requiredErrorMsg: PERSONAL_DETAILS_PHRASES.AGE_REQUIRED_ERROR,
        },
    },
    [RegistrationStep.ProfessionalDetails]: {
        specialtyId: {
            name: 'specialtyId',
            label: PROFESSIONAL_DETAILS_PHRASES.SELECT_SPECIALTY,
        },
        medicalNumber: {
            name: 'medicalNumber',
            label: PROFESSIONAL_DETAILS_PHRASES.MEDICAL_NUMBER,
        },
        stateMedicalCouncil: {
            name: 'stateMedicalCouncil',
            label: PROFESSIONAL_DETAILS_PHRASES.STATE_MEDICAL_COUNCIL,
        },
        primaryWorkplace: {
            name: 'primaryWorkplace',
            label: PROFESSIONAL_DETAILS_PHRASES.PRIMARY_WORKPLACE_NAME,
        },
        primaryWorkplacePhone: {
            name: 'primaryWorkplacePhone',
            label: PROFESSIONAL_DETAILS_PHRASES.WORKPLACE_MAINLINE_NUMBER,
        },
        professionalIdNumber: {
            name: 'professionalIdNumber',
            label: PROFESSIONAL_DETAILS_PHRASES.PROFESSIONAL_ID_NUMBER,
        },
        medicalNumberNPI: {
            name: 'medicalNumberNPI',
            label: PROFESSIONAL_DETAILS_PHRASES.NPI_NUMBER,
        },
        medicalNumberOptOut: {
            name: 'medicalNumberOptOut',
            label: PROFESSIONAL_DETAILS_PHRASES.WILL_NOT_PROVIDE_MEDICAL_NUMBER,
        },
        graduationYear: {
            name: 'graduationYear',
            label: PROFESSIONAL_DETAILS_PHRASES.YEAR_OF_GRADUATE,
        },
        medicalSchool: {
            name: 'medicalSchool',
            label: PROFESSIONAL_DETAILS_PHRASES.WHAT_MEDICAL_SCHOOL,
        },
        stateCodeLicensed: {
            name: 'stateCodeLicensed',
            label: PROFESSIONAL_DETAILS_PHRASES.WHICH_STATE_OF_PRACTICE,
        },
    },
    [RegistrationStep.ContactDetails]: {
        captchaResponse: {
            name: 'captchaResponse',
            label: CONTACT_DETAILS_PHRASES.CAPTCHA_RESPONSE,
            requiredErrorMsg: CONTACT_DETAILS_PHRASES.CAPTCHA_RESPONSE_REQUIRED_ERROR,
        },
        mobilePhone: {
            name: 'mobilePhone',
            label: CONTACT_DETAILS_PHRASES.MOBILE_PHONE,
            requiredErrorMsg: CONTACT_DETAILS_PHRASES.MOBILE_PHONE_INVALID_ERROR,
            invalidErrorMsg: CONTACT_DETAILS_PHRASES.MOBILE_PHONE_INVALID_ERROR,
        },
        mobilePhoneCode: {
            name: 'mobilePhoneCode',
            label: CONTACT_DETAILS_PHRASES.TYPE_CODE,
            requiredErrorMsg: FORM_ERROR_PHRASES.REQUIRED_FIELD,
            invalidErrorMsg: FORM_ERROR_PHRASES.ENTER_VALID_PHONE,
        },
        alternativePhone: {
            name: 'alternativePhone',
            label: CONTACT_DETAILS_PHRASES.ALTERNATIVE_PHONE,
        },
        SMSOptIn: {
            name: 'SMSOptIn',
            label: CONTACT_DETAILS_PHRASES.SMS_OPTIN,
        },
    },
    [RegistrationStep.Identity]: {},
    [RegistrationStep.Address]: {
        address1: {
            name: 'address1',
            label: ADDRESS_PHRASES.ADDRESS,
            requiredErrorMsg: FORM_ERROR_PHRASES.REQUIRED_FIELD,
        },
        zip: {
            name: 'zip',
            label: ADDRESS_PHRASES.ZIP_CODE,
            requiredErrorMsg: FORM_ERROR_PHRASES.REQUIRED_FIELD,
        },
        city: {
            name: 'city',
            label: ADDRESS_PHRASES.CITY,
            requiredErrorMsg: FORM_ERROR_PHRASES.REQUIRED_FIELD,
        },
        stateCode: {
            name: 'stateCode',
            label: ADDRESS_PHRASES.STATE,
            requiredErrorMsg: FORM_ERROR_PHRASES.REQUIRED_FIELD,
        },
        countryCode: {
            name: 'countryCode',
            label: ADDRESS_PHRASES.COUNTRY,
            requiredErrorMsg: FORM_ERROR_PHRASES.REQUIRED_FIELD,
        },
    },
} as const;
